import api from '@/api/api';
import { createQueryParams } from '@/utils/createQueryParams';

const DOMAIN = '/forms';

const getAll = async (
	params?: Forms.ListFormsAdminParams,
): Promise<Forms.PaginatedAdminForms> => {
	const queryParams = createQueryParams({ ...(params ?? {}) });
	const queryString = queryParams.toString();
	const url = queryString ? `${DOMAIN}?${queryString}` : DOMAIN;
	const { data } = await api.get(url);
	return data;
};

const getById = async (id: string): Promise<Forms.FormAdminDetail> => {
	const { data } = await api.get(`${DOMAIN}/${id}`);
	return data;
};

const getAvailable = async (params?: {
	page?: number;
	limit?: number;
}): Promise<Forms.PaginatedAvailableForms> => {
	const queryParams = createQueryParams(params ?? {});
	const queryString = queryParams.toString();
	const url = queryString
		? `${DOMAIN}/available?${queryString}`
		: `${DOMAIN}/available`;
	const { data } = await api.get(url);
	return data;
};

/** Portal empresa autenticado (`GET /forms/available/company/me`). */
const getAvailableForCompany = async (params?: {
	page?: number;
	limit?: number;
}): Promise<Forms.PaginatedCompanyForms> => {
	const queryParams = createQueryParams(params ?? {});
	const queryString = queryParams.toString();
	const url = queryString
		? `${DOMAIN}/available/company/me?${queryString}`
		: `${DOMAIN}/available/company/me`;
	const { data } = await api.get(url);
	return data;
};

/** Colaborador autenticado após POST /employees/login (`/empresa/formulario/:companyId`). */
const getAvailableForEmployee = async (params?: {
	page?: number;
	limit?: number;
}): Promise<Forms.PaginatedAvailableForms> => {
	const queryParams = createQueryParams(params ?? {});
	const queryString = queryParams.toString();
	const url = queryString
		? `${DOMAIN}/available/employee/me?${queryString}`
		: `${DOMAIN}/available/employee/me`;
	const { data } = await api.get(url);
	return data;
};

const getMyAnswers = async (formId: string): Promise<Forms.FormAnswer[]> => {
	const { data } = await api.get(`${DOMAIN}/${formId}/my-answers`);
	return data;
};

const getMyAnswersAnalytics =
	async (): Promise<Forms.FormMyAnswersAnalytics> => {
		const { data } = await api.get(`${DOMAIN}/answers/me/analytics`);
		return data;
	};

const getAnalytics = async (
	formId: string,
	params: Forms.FindFormAnalyticsQueryParams,
): Promise<Forms.FindFormAnalyticsResponse> => {
	const queryParams = createQueryParams({ ...params });
	const queryString = queryParams.toString();
	const url = `${DOMAIN}/${formId}/analytics?${queryString}`;
	const { data } = await api.get(url);
	return data;
};

const submitAnswer = async (
	formId: string,
	body: Forms.SubmitFormAnswer,
): Promise<unknown> => {
	const { data } = await api.post(`${DOMAIN}/${formId}/answers`, body);
	return data;
};

const create = async (body: Forms.CreateForm): Promise<Forms.Form> => {
	const { data } = await api.post(DOMAIN, body);
	return data;
};

const patch = async (
	id: string,
	body: Forms.UpdateForm,
): Promise<Forms.Form> => {
	const { data } = await api.patch(`${DOMAIN}/${id}`, body);
	return data;
};

const remove = async (id: string): Promise<void> => {
	await api.delete(`${DOMAIN}/${id}`);
};

const addQuestion = async (
	formId: string,
	body: Forms.CreateFormQuestion,
): Promise<Forms.FormQuestion> => {
	const { data } = await api.post(`${DOMAIN}/${formId}/questions`, body);
	return data;
};

const patchQuestion = async (
	formId: string,
	questionId: string,
	body: Forms.UpdateFormQuestion,
): Promise<Forms.FormQuestion> => {
	const { data } = await api.patch(
		`${DOMAIN}/${formId}/questions/${questionId}`,
		body,
	);
	return data;
};

const removeQuestion = async (
	formId: string,
	questionId: string,
): Promise<Forms.FormQuestion> => {
	const { data } = await api.delete(
		`${DOMAIN}/${formId}/questions/${questionId}`,
	);
	return data;
};

const reorderQuestions = async (
	formId: string,
	body: Forms.ReorderFormQuestions,
): Promise<Forms.FormQuestion[]> => {
	const { data } = await api.put(`${DOMAIN}/${formId}/questions/order`, body);
	return data;
};

const getAnswers = async (
	formId: string,
	params?: { page?: number; limit?: number },
): Promise<Forms.PaginatedFormAnswersAdmin> => {
	const queryParams = createQueryParams(params ?? {});
	const queryString = queryParams.toString();
	const url = queryString
		? `${DOMAIN}/${formId}/answers?${queryString}`
		: `${DOMAIN}/${formId}/answers`;
	const { data } = await api.get(url);
	return data;
};

const addAudience = async (
	formId: string,
	body: Forms.CreateFormAudience,
): Promise<Forms.FormAudience> => {
	const { data } = await api.post(`${DOMAIN}/${formId}/audiences`, body);
	return data;
};

const removeAudience = async (
	formId: string,
	audienceId: string,
): Promise<void> => {
	await api.delete(`${DOMAIN}/${formId}/audiences/${audienceId}`);
};

const getApplicationPeriods = async (
	formId: string,
	companyId: string,
	params?: { page?: number; limit?: number; activeNow?: boolean },
): Promise<Forms.PaginatedFormApplicationPeriods> => {
	const queryParams = createQueryParams(params ?? {});
	const queryString = queryParams.toString();
	const url = queryString
		? `${DOMAIN}/${formId}/companies/${companyId}/application-periods?${queryString}`
		: `${DOMAIN}/${formId}/companies/${companyId}/application-periods`;
	const { data } = await api.get(url);
	return data;
};

const createApplicationPeriod = async (
	formId: string,
	companyId: string,
	body: Forms.CreateFormApplicationPeriod,
): Promise<Forms.FormApplicationPeriod> => {
	const { data } = await api.post(
		`${DOMAIN}/${formId}/companies/${companyId}/application-periods`,
		body,
	);
	return data;
};

const patchApplicationPeriod = async (
	periodId: string,
	body: Forms.UpdateFormApplicationPeriod,
): Promise<Forms.FormApplicationPeriod> => {
	const { data } = await api.patch(
		`${DOMAIN}/application-periods/${periodId}`,
		body,
	);
	return data;
};

const FormService = {
	getAll,
	getById,
	getAvailable,
	getAvailableForCompany,
	getAvailableForEmployee,
	getMyAnswers,
	getMyAnswersAnalytics,
	getAnalytics,
	create,
	patch,
	remove,
	addQuestion,
	patchQuestion,
	removeQuestion,
	reorderQuestions,
	getAnswers,
	addAudience,
	removeAudience,
	getApplicationPeriods,
	createApplicationPeriod,
	patchApplicationPeriod,
	submitAnswer,
};

export { FormService };
